(() => {
  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const routes = window.themeRoutes || {};
  const strings = window.themeStrings || {};

  const formatMoney = (cents) => {
    const currency = window.Shopify?.currency?.active || 'BRL';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency }).format((cents || 0) / 100);
  };

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;'
  })[character]);

  const installmentMessage = (price, count, template) => String(template || 'Em até [count]x de [value] sem juros')
    .split('[count]').join(String(count))
    .split('[value]').join(formatMoney(Math.round(price / count)));

  const drawer = () => $('[data-cart-drawer]');
  const drawerEnabled = () => drawer()?.dataset.enabled === 'true';
  const isGiftItem = (item) => {
    const properties = item?.properties || {};
    return String(properties._ivory_gift || properties._ivr_gift || '').toLowerCase() === 'true';
  };
  let previouslyFocused;
  let giftSyncing = false;

  const imageUrl = (source, width = 240) => {
    if (!source) return '';
    try {
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return source;
    }
  };

  const setDrawerState = (drawer, open) => {
    const overlay = $('[data-drawer-overlay]');
    if (!drawer) return;
    const wasOpen = drawer.classList.contains('is-open');
    if (open) previouslyFocused = document.activeElement;
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    if (overlay) overlay.hidden = !open;
    document.body.classList.toggle('drawer-open', open);
    if (open) {
      window.requestAnimationFrame(() => $('[data-cart-close]', drawer)?.focus());
    } else if (wasOpen && previouslyFocused instanceof HTMLElement) {
      previouslyFocused.focus();
    }
  };

  const showToast = (message) => {
    const toast = $('[data-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(showToast.timeout);
    showToast.timeout = window.setTimeout(() => toast.classList.remove('is-visible'), 2600);
  };

  const renderProperties = (item) => {
    if (!item.properties) return '';
    const properties = Object.entries(item.properties).filter(([key, value]) => key.charAt(0) !== '_' && value !== '' && value != null);
    if (!properties.length) return '';
    return `<dl class="cart-line__properties">${properties.map(([key, value]) => `<div><dt>${escapeHtml(key)}:</dt><dd>${escapeHtml(value)}</dd></div>`).join('')}</dl>`;
  };

  const renderDiscounts = (item) => {
    if (!item.discount_allocations?.length) return '';
    return `<ul class="cart-line__discounts">${item.discount_allocations.map((allocation) => `<li>${escapeHtml(allocation.discount_application?.title || '')} (−${formatMoney(allocation.amount)})</li>`).join('')}</ul>`;
  };

  const updateShippingGoals = (total) => {
    $$('[data-shipping-goal]').forEach((goal) => {
      const threshold = Number(goal.dataset.threshold || 0);
      const remaining = Math.max(0, threshold - total);
      const progress = threshold ? Math.min(100, (total / threshold) * 100) : 100;
      const bar = $('[data-shipping-progress]', goal);
      const message = $('[data-shipping-message]', goal);
      if (bar) bar.style.width = `${progress}%`;
      if (message) {
        const template = remaining === 0 ? goal.dataset.successText : goal.dataset.beforeText;
        message.textContent = String(template || '').split('[value]').join(formatMoney(remaining));
      }
      goal.classList.toggle('is-complete', remaining === 0);
    });
  };

  const qualifyingTotal = (cart) => cart.items.reduce((total, item) => total + (isGiftItem(item) ? 0 : item.final_line_price), 0);

  const updateGiftBlocks = (cart) => {
    const total = qualifyingTotal(cart);
    $$('[data-gift-block]').forEach((gift) => {
      const threshold = Number(gift.dataset.threshold || 0);
      const remaining = Math.max(0, threshold - total);
      const unlocked = remaining === 0;
      const message = $('[data-gift-message]', gift);
      const template = unlocked ? gift.dataset.unlockedText : gift.dataset.lockedText;
      if (message && gift.dataset.autoFailed !== 'true') message.textContent = String(template || '').split('[value]').join(formatMoney(remaining));
      gift.classList.toggle('is-unlocked', unlocked);
      const addButton = $('[data-gift-add]', gift);
      const alreadyAdded = cart.items.some((item) => isGiftItem(item) && String(item.variant_id) === gift.dataset.variantId);
      if (addButton) addButton.disabled = !unlocked || alreadyAdded;
    });
  };

  const updateOrderBumps = (cart) => {
    $$('[data-order-bump]').forEach((offer) => {
      const selectedVariant = $('[data-order-bump-variant]', offer)?.value || offer.dataset.variantId;
      const inCart = cart.items.some((item) => String(item.variant_id) === selectedVariant);
      offer.hidden = inCart && offer.dataset.hideIfAdded === 'true';
      offer.classList.toggle('is-added', inCart);
    });
  };

  const updateCheckoutState = () => {
    const checkout = $('[data-cart-checkout]');
    const requiredTerms = $$('[data-cart-terms][data-required="true"]');
    const accepted = requiredTerms.every((terms) => $('[data-cart-terms-checkbox]', terms)?.checked);
    if (!checkout) return;
    checkout.classList.toggle('is-disabled', !accepted);
    checkout.setAttribute('aria-disabled', String(!accepted));
  };

  const renderCart = (cart) => {
    const cartDrawer = drawer();
    const container = $('[data-cart-items]', cartDrawer || document);
    const footer = $('[data-cart-footer]', cartDrawer || document);
    const body = $('[data-cart-body]', cartDrawer || document);
    const countElements = $$('[data-cart-count], [data-cart-drawer-count]');
    countElements.forEach((element) => {
      element.textContent = element.hasAttribute('data-cart-drawer-count') ? `(${cart.item_count})` : cart.item_count;
      element.hidden = cart.item_count === 0 && element.hasAttribute('data-cart-count');
    });
    if (body) body.dataset.cartEmpty = String(cart.item_count === 0);
    if (!container) return;

    if (!cart.items.length) {
      const emptyTitle = cartDrawer?.dataset.emptyTitle || strings.empty;
      const emptyText = cartDrawer?.dataset.emptyText || '';
      const continueLabel = cartDrawer?.dataset.continueLabel || strings.continue;
      container.innerHTML = `<div class="cart-empty"><h3>${escapeHtml(emptyTitle)}</h3>${emptyText ? `<p>${escapeHtml(emptyText)}</p>` : ''}<a class="button button--outline" href="${escapeHtml(routes.allProducts || '/collections/all')}">${escapeHtml(continueLabel)}</a></div>`;
      if (footer) footer.hidden = true;
    } else {
      const showVendor = cartDrawer?.dataset.showVendor === 'true';
      const showVariant = cartDrawer?.dataset.showVariant === 'true';
      const showProperties = cartDrawer?.dataset.showProperties === 'true';
      const showDiscounts = cartDrawer?.dataset.showDiscounts === 'true';
      const showOriginalPrice = cartDrawer?.dataset.showOriginalPrice === 'true';
      const variantPosition = cartDrawer?.dataset.variantPosition || 'footer';
      const showItemInstallments = cartDrawer?.dataset.showItemInstallments === 'true';
      const itemInstallmentsCount = Number(cartDrawer?.dataset.itemInstallmentsCount || 10);
      const itemInstallmentsText = cartDrawer?.dataset.itemInstallmentsText;
      const showManualBadge = cartDrawer?.dataset.showManualDiscountBadge === 'true';
      const manualDiscountPercent = Number(cartDrawer?.dataset.manualDiscountPercent || 0);
      const manualDiscountLabel = cartDrawer?.dataset.manualDiscountLabel || 'OFF';
      const quantityLabel = cartDrawer?.dataset.quantityLabel || 'Quantidade';
      container.innerHTML = cart.items.map((item) => {
        const giftItem = isGiftItem(item);
        const showTitleVariant = showVariant && item.variant_title && item.variant_title !== 'Default Title' && variantPosition === 'title';
        const showFooterVariant = showVariant && item.variant_title && item.variant_title !== 'Default Title' && variantPosition === 'footer';

        return `
        <article class="cart-line${giftItem ? ' cart-line--gift' : ''}"${giftItem ? ' data-cart-gift="true"' : ''}>
          <a class="cart-line__media" href="${escapeHtml(item.url)}">${item.image ? `<img src="${escapeHtml(imageUrl(item.image))}" alt="${escapeHtml(item.product_title)}" width="92" height="138">` : ''}</a>
          <div class="cart-line__content">
            <div class="cart-line__heading">
              <div>
                ${showVendor && item.vendor ? `<p class="cart-line__vendor">${escapeHtml(item.vendor)}</p>` : ''}
                <h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></h3>
                ${giftItem ? '<span class="price-badge cart-line__gift-badge">BRINDE</span>' : ''}
              </div>
              <button class="cart-line__remove" type="button" data-cart-change="${escapeHtml(item.key)}" data-quantity="0" aria-label="${escapeHtml(strings.remove)}: ${escapeHtml(item.product_title)}"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3m-9 0 1 14h10l1-14M10 11v6m4-6v6"/></svg><span class="cart-line__remove-label">${escapeHtml(strings.remove)}</span></button>
            </div>
            ${showTitleVariant ? `<p class="cart-line__variant">${escapeHtml(item.variant_title)}</p>` : ''}
            ${item.selling_plan_allocation?.selling_plan?.name ? `<p class="cart-line__plan">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>` : ''}
            ${showProperties ? renderProperties(item) : ''}
            ${!giftItem && showDiscounts ? renderDiscounts(item) : ''}
            ${giftItem ? '' : `<div class="cart-line__prices">${showOriginalPrice && item.original_line_price > item.final_line_price ? `<s>${formatMoney(item.original_line_price)}</s>` : ''}<span class="cart-line__price">${formatMoney(item.final_line_price)}</span>${showManualBadge && manualDiscountPercent > 0 ? `<em class="price-badge">${manualDiscountPercent}% ${escapeHtml(manualDiscountLabel)}</em>` : ''}</div>`}
            ${!giftItem && showItemInstallments ? `<p class="payment-terms cart-line__installments">${escapeHtml(installmentMessage(item.final_line_price, itemInstallmentsCount, itemInstallmentsText))}</p>` : ''}
            ${giftItem
              ? (showFooterVariant ? `<div class="cart-line__bottom"><div class="cart-line__purchase-controls"><p class="cart-line__variant">${escapeHtml(item.variant_title)}</p></div></div>` : '')
              : `<div class="cart-line__bottom">
                  <div class="cart-line__purchase-controls">
                    <div class="quantity">
                      <button type="button" data-cart-change="${escapeHtml(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="${escapeHtml(quantityLabel)}: menos">−</button>
                      <input value="${item.quantity}" readonly aria-label="${escapeHtml(quantityLabel)}">
                      <button type="button" data-cart-change="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="${escapeHtml(quantityLabel)}: mais">+</button>
                    </div>
                    ${showFooterVariant ? `<p class="cart-line__variant">${escapeHtml(item.variant_title)}</p>` : ''}
                  </div>
                </div>`
            }
          </div>
        </article>`;
      }).join('');
      if (footer) footer.hidden = false;
    }

    const subtotal = $('[data-cart-subtotal]', cartDrawer || document);
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    const discountRow = $('[data-cart-discount-total]', cartDrawer || document);
    const discountValue = $('[data-cart-discount-value]', cartDrawer || document);
    if (discountRow) discountRow.hidden = !cart.total_discount;
    if (discountValue) discountValue.textContent = `−${formatMoney(cart.total_discount)}`;
    const footerInstallments = $('[data-cart-footer-installments]', cartDrawer || document);
    if (footerInstallments) footerInstallments.textContent = installmentMessage(cart.total_price, Number(footerInstallments.dataset.count || 10), footerInstallments.dataset.template);
    updateShippingGoals(cart.total_price);
    updateGiftBlocks(cart);
    updateOrderBumps(cart);
    updateCheckoutState();
  };

  const parseCartResponse = async (response, fallbackMessage) => {
    if (response.ok) return response.json();
    let error;
    try { error = await response.json(); } catch (parseError) { error = {}; }
    throw new Error(error.description || error.message || fallbackMessage);
  };

  const fetchCart = async () => {
    const response = await fetch(`${routes.cart || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    return parseCartResponse(response, drawer()?.dataset.updateError || 'Cart request failed');
  };

  const syncAutomaticGifts = async (initialCart) => {
    const gifts = $$('[data-gift-block][data-auto-add="true"]');
    if (!gifts.length || giftSyncing) return initialCart;
    giftSyncing = true;
    let cart = initialCart;
    try {
      for (const gift of gifts) {
        const variantId = gift.dataset.variantId;
        const threshold = Number(gift.dataset.threshold || 0);
        const eligible = qualifyingTotal(cart) >= threshold;
        const giftLine = cart.items.find((item) => isGiftItem(item) && String(item.variant_id) === variantId);
        const shouldAdd = eligible && !giftLine;
        const shouldRemove = giftLine && !eligible && gift.dataset.removeBelow === 'true';
        if (!shouldAdd && !shouldRemove) continue;

        try {
          const response = shouldAdd
            ? await fetch(`${routes.cartAdd || '/cart/add'}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { _ivory_gift: 'true', _ivory_gift_id: gift.dataset.blockId } }] })
            })
            : await fetch(`${routes.cartChange || '/cart/change'}.js`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: giftLine.key, quantity: 0 })
            });
          await parseCartResponse(response, gift.dataset.giftError || drawer()?.dataset.giftError);
          gift.dataset.autoFailed = 'false';
          cart = await fetchCart();
        } catch (error) {
          gift.dataset.autoFailed = 'true';
          gift.classList.add('has-error');
          const message = $('[data-gift-message]', gift);
          if (message) message.textContent = drawer()?.dataset.giftError || error.message;
        }
      }
      return cart;
    } finally {
      giftSyncing = false;
    }
  };

  const reconcileCart = async (cart, syncGift = true) => {
    renderCart(cart);
    if (!syncGift) return cart;
    const finalCart = await syncAutomaticGifts(cart);
    if (finalCart !== cart) renderCart(finalCart);
    return finalCart;
  };

  const getCart = async () => reconcileCart(await fetchCart());

  const changeCart = async (id, quantity) => {
    const response = await fetch(`${routes.cartChange || '/cart/change'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id, quantity })
    });
    return reconcileCart(await parseCartResponse(response, drawer()?.dataset.updateError || 'Cart update failed'));
  };

  const addItems = async (items) => {
    const response = await fetch(`${routes.cartAdd || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });
    await parseCartResponse(response, drawer()?.dataset.updateError);
    return getCart();
  };

  const addVariant = (variantId, properties = {}) => addItems([{ id: variantId, quantity: 1, properties }]);

  const saveCartNote = async (note, status) => {
    if (status) status.textContent = drawer()?.dataset.noteSaving || '';
    const response = await fetch(`${routes.cartUpdate || '/cart/update'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note })
    });
    await parseCartResponse(response, drawer()?.dataset.updateError);
    if (status) status.textContent = drawer()?.dataset.noteSaved || '';
  };

  const addToCart = async (form) => {
    const button = $('[type="submit"]', form);
    if (button) button.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${routes.cartAdd || '/cart/add'}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || error.message || 'Não foi possível adicionar o produto.');
      }
      await getCart();
      showToast(strings.added);
      if (drawerEnabled()) setDrawerState(drawer(), true);
      else window.location.assign(routes.cart || '/cart');
    } catch (error) {
      showToast(error.message);
    } finally {
      if (button) button.removeAttribute('aria-busy');
    }
  };

  const initDrawers = () => {
    const mobileMenu = $('[data-mobile-menu]');
    const searchPanel = $('[data-search-panel]');
    $$('[data-cart-toggle]:not([data-cart-toggle-bound])').forEach((button) => button.addEventListener('click', (event) => {
      if (!drawerEnabled()) return;
      event.preventDefault();
      getCart().catch(() => {});
      setDrawerState(drawer(), true);
    }));
    $$('[data-cart-toggle]').forEach((button) => { button.dataset.cartToggleBound = 'true'; });
    const closeAll = () => {
      setDrawerState(drawer(), false);
      mobileMenu?.classList.remove('is-open');
      searchPanel?.classList.remove('is-open');
      $('[data-facets-drawer]')?.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
    };
    $$('[data-cart-close]:not([data-cart-close-bound]), [data-drawer-overlay]:not([data-cart-close-bound])').forEach((button) => {
      button.addEventListener('click', closeAll);
      button.dataset.cartCloseBound = 'true';
    });
    const menuToggle = $('[data-mobile-menu-toggle]:not([data-mobile-menu-bound])');
    menuToggle?.addEventListener('click', () => {
      mobileMenu?.classList.add('is-open');
      $('[data-drawer-overlay]').hidden = false;
      document.body.classList.add('drawer-open');
    });
    if (menuToggle) menuToggle.dataset.mobileMenuBound = 'true';
    const menuClose = $('[data-mobile-menu-close]:not([data-mobile-menu-bound])');
    menuClose?.addEventListener('click', () => {
      mobileMenu?.classList.remove('is-open');
      $('[data-drawer-overlay]').hidden = true;
      document.body.classList.remove('drawer-open');
    });
    if (menuClose) menuClose.dataset.mobileMenuBound = 'true';
    $$('[data-search-toggle]:not([data-search-bound])').forEach((button) => button.addEventListener('click', () => {
      searchPanel?.classList.toggle('is-open');
      searchPanel?.setAttribute('aria-hidden', String(!searchPanel.classList.contains('is-open')));
      if (searchPanel?.classList.contains('is-open')) $('[name="q"]', searchPanel)?.focus();
    }));
    $$('[data-search-toggle]').forEach((button) => { button.dataset.searchBound = 'true'; });

    $$('[data-cart-terms-checkbox]:not([data-terms-bound])').forEach((checkbox) => checkbox.addEventListener('change', () => {
      checkbox.closest('[data-cart-terms]')?.querySelector('[data-cart-terms-error]')?.setAttribute('hidden', '');
      updateCheckoutState();
    }));
    $$('[data-cart-terms-checkbox]').forEach((checkbox) => { checkbox.dataset.termsBound = 'true'; });

    const checkout = $('[data-cart-checkout]:not([data-checkout-bound])');
    checkout?.addEventListener('click', (event) => {
      const missing = $$('[data-cart-terms][data-required="true"]').find((terms) => !$('[data-cart-terms-checkbox]', terms)?.checked);
      if (!missing) return;
      event.preventDefault();
      $('[data-cart-terms-error]', missing)?.removeAttribute('hidden');
      $('[data-cart-terms-checkbox]', missing)?.focus();
    });
    if (checkout) checkout.dataset.checkoutBound = 'true';
  };

  const initHeader = () => {
    const header = $('[data-site-header]');
    if (!header) return;
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 32);
    update();
    window.addEventListener('scroll', update, { passive: true });
  };

  const initHeroSliders = () => {
    $$('[data-hero-slider]').forEach((slider) => {
      const track = $('[data-hero-track]', slider);
      const slides = $$('[data-hero-slide]', slider);
      const dots = $$('[data-hero-dot]', slider);
      let index = 0;
      let timer;
      const goTo = (next) => {
        index = (next + slides.length) % slides.length;
        if (track) track.style.transform = `translateX(-${index * 100}%)`;
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
      };
      $('[data-hero-prev]', slider)?.addEventListener('click', () => goTo(index - 1));
      $('[data-hero-next]', slider)?.addEventListener('click', () => goTo(index + 1));
      dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => goTo(dotIndex)));
      if (slides.length > 1 && slider.dataset.autoplay === 'true') {
        const start = () => { timer = window.setInterval(() => goTo(index + 1), Number(slider.dataset.interval || 6000)); };
        const stop = () => window.clearInterval(timer);
        slider.addEventListener('mouseenter', stop);
        slider.addEventListener('mouseleave', start);
        start();
      }
    });
  };

  const initCarousels = () => {
    $$('[data-carousel-control]').forEach((button) => button.addEventListener('click', () => {
      const carousel = document.getElementById(button.dataset.carouselControl);
      const direction = button.dataset.direction === 'next' ? 1 : -1;
      carousel?.scrollBy({ left: direction * carousel.clientWidth * 0.78, behavior: 'smooth' });
    }));
  };

  const initProduct = () => {
    $$('[data-product-root]').forEach((root) => {
      const dataElement = $('[data-product-json]', root);
      if (!dataElement) return;
      const product = JSON.parse(dataElement.textContent);
      const optionGroups = $$('[data-option-position]', root);
      const variantInputs = $$('[data-product-variant-input]', root);
      const quantityInputs = $$('[data-product-quantity-input]', root);
      const quantityDisplays = $$('[data-product-quantity-display]', root);
      const shippingCalculators = $$('[data-shipping-calculator][data-shipping-mode="frenet"]', root);
      const selected = optionGroups.map((group) => $('[data-option-select]', group)?.value || $('.is-selected', group)?.dataset.optionValue || '');

      const selectedVariant = () => {
        const selectedId = Number(variantInputs[0]?.value || 0);
        return product.variants.find((variant) => Number(variant.id) === selectedId) || product.variants[0];
      };

      const clearShippingResults = () => {
        shippingCalculators.forEach((calculator) => {
          const results = $('[data-shipping-results]', calculator);
          if (!results) return;
          results.innerHTML = '';
          results.className = 'shipping-calculator__results';
        });
      };

      const updateQuantity = (next) => {
        const quantity = Math.max(1, Number.parseInt(next, 10) || 1);
        quantityInputs.forEach((input) => { input.value = quantity; });
        quantityDisplays.forEach((input) => { input.value = quantity; });
        clearShippingResults();
      };

      const selectVariant = () => {
        const variant = product.variants.find((item) => item.options.every((option, position) => option === selected[position]));
        if (!variant) return;
        variantInputs.forEach((input) => { input.value = variant.id; });
        const price = $('[data-product-price]', root);
        const compare = $('[data-product-compare]', root);
        if (price) price.textContent = formatMoney(variant.price);
        if (compare) {
          compare.textContent = formatMoney(variant.compare_at_price);
          compare.hidden = !variant.compare_at_price || variant.compare_at_price <= variant.price;
        }
        const priceWrapper = $('[data-price-wrapper]', root);
        priceWrapper?.classList.toggle('price--sale', Boolean(variant.compare_at_price && variant.compare_at_price > variant.price));
        const badge = $('[data-product-discount-badge]', root);
        if (badge) {
          const automaticPercent = variant.compare_at_price > variant.price ? Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price) : 0;
          const percent = badge.dataset.mode === 'manual' ? Number(badge.dataset.manualPercent || 0) : automaticPercent;
          badge.hidden = badge.dataset.mode === 'hidden' || percent <= 0;
          badge.textContent = `${percent}% ${badge.dataset.label || 'OFF'}`;
        }
        const submit = $('[data-product-submit]', root);
        if (submit) {
          submit.disabled = !variant.available;
          submit.textContent = variant.available ? submit.dataset.availableText : submit.dataset.soldText;
        }
        $$('[data-installment-text]', root).forEach((installment) => {
          const count = Number(installment.dataset.count || 10);
          const text = $('span', installment);
          if (text) text.textContent = installmentMessage(variant.price, count, installment.dataset.template);
        });
        const cashback = $('[data-cashback-value]', root);
        if (cashback) cashback.textContent = formatMoney(Math.round(variant.price * Number(cashback.dataset.percent || 0) / 100));
        if (variant.featured_media) {
          const media = $(`[data-media-id="${variant.featured_media.id}"]`, root);
          media?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
        clearShippingResults();
        window.history.replaceState({}, '', `${window.location.pathname}?variant=${variant.id}`);
      };

      const decimalNumber = (value) => {
        if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
        let normalized = String(value ?? '').trim().replace(/[^\d,.-]/g, '');
        if (normalized.includes(',') && normalized.includes('.')) {
          normalized = normalized.lastIndexOf(',') > normalized.lastIndexOf('.')
            ? normalized.replace(/\./g, '').replace(',', '.')
            : normalized.replace(/,/g, '');
        } else if (normalized.includes(',')) {
          normalized = normalized.replace(',', '.');
        }
        const number = Number(normalized);
        return Number.isFinite(number) ? number : 0;
      };

      const shippingServices = (payload) => {
        if (Array.isArray(payload)) return payload;
        const direct = payload?.ShippingSevicesArray
          || payload?.ShippingServicesArray
          || payload?.shippingServices
          || payload?.services
          || payload?.quotes
          || payload?.data;
        if (Array.isArray(direct)) return direct;
        if (direct && typeof direct === 'object') return shippingServices(direct);
        return [];
      };

      const renderShippingMessage = (calculator, type, message) => {
        const results = $('[data-shipping-results]', calculator);
        if (!results) return;
        results.className = `shipping-calculator__results shipping-calculator__results--${type}`;
        results.innerHTML = `<p>${escapeHtml(message)}</p>`;
      };

      const renderShippingRates = (calculator, payload) => {
        const results = $('[data-shipping-results]', calculator);
        if (!results) return;
        const limit = Math.max(1, Number(calculator.dataset.resultsLimit || 4));
        const services = shippingServices(payload).filter((service) => {
          const error = service?.Error ?? service?.error ?? service?.hasError;
          return !error && service && typeof service === 'object';
        }).slice(0, limit);
        if (!services.length) {
          renderShippingMessage(calculator, 'empty', calculator.dataset.emptyText || 'Nenhuma opção de entrega encontrada para este CEP.');
          return;
        }
        const deliveryTemplate = calculator.dataset.deliveryTemplate || 'Entrega em até [days] dias úteis';
        results.className = 'shipping-calculator__results shipping-calculator__results--rates';
        results.innerHTML = services.map((service) => {
          const name = service.ServiceDescription || service.serviceDescription || service.name || service.service || 'Entrega';
          const carrier = service.Carrier || service.carrier || service.carrierName || '';
          const cents = service.priceCents != null
            ? Math.round(decimalNumber(service.priceCents))
            : Math.round(decimalNumber(service.ShippingPrice ?? service.shippingPrice ?? service.price ?? service.value) * 100);
          const days = decimalNumber(service.DeliveryTime ?? service.deliveryTime ?? service.deliveryDays ?? service.deadline);
          const price = cents <= 0 ? 'Grátis' : formatMoney(cents);
          const delivery = days > 0 ? deliveryTemplate.split('[days]').join(String(Math.ceil(days))) : '';
          return `<article class="shipping-rate"><div class="shipping-rate__service"><strong>${escapeHtml(name)}</strong>${carrier ? `<span>${escapeHtml(carrier)}</span>` : ''}</div><div class="shipping-rate__details"><strong>${escapeHtml(price)}</strong>${delivery ? `<span>${escapeHtml(delivery)}</span>` : ''}</div></article>`;
        }).join('');
      };

      shippingCalculators.forEach((calculator) => {
        const form = $('[data-shipping-form]', calculator);
        const zipInput = $('[data-shipping-zip]', calculator);
        const submit = $('[data-shipping-submit]', calculator);
        if (!form || !zipInput) return;
        zipInput.addEventListener('input', () => {
          const digits = zipInput.value.replace(/\D/g, '').slice(0, 8);
          zipInput.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
          clearShippingResults();
        });
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          const recipientZipCode = zipInput.value.replace(/\D/g, '');
          const sellerZipCode = String(calculator.dataset.sellerZip || '').replace(/\D/g, '');
          const endpoint = calculator.dataset.shippingEndpoint || '';
          const errorText = calculator.dataset.errorText || 'Não foi possível calcular o frete. Confira o CEP e tente novamente.';
          if (recipientZipCode.length !== 8) {
            renderShippingMessage(calculator, 'error', errorText);
            zipInput.focus();
            return;
          }
          if (sellerZipCode.length !== 8 || !endpoint) {
            renderShippingMessage(calculator, 'error', 'Configure o CEP de origem e o endpoint seguro da Frenet no editor do tema.');
            return;
          }
          const variant = selectedVariant();
          if (!variant) {
            renderShippingMessage(calculator, 'error', errorText);
            return;
          }
          const quantity = Math.max(1, Number.parseInt(quantityInputs[0]?.value || quantityDisplays[0]?.value || '1', 10));
          const fallbackWeight = Math.max(1, Number(calculator.dataset.fallbackWeight || 500));
          const itemWeight = Math.max(1, Number(variant.grams || fallbackWeight));
          const payload = {
            sellerZipCode,
            recipientZipCode,
            country: 'BR',
            currency: calculator.dataset.currency || 'BRL',
            invoiceValue: (Number(variant.price || 0) * quantity) / 100,
            items: [{
              productId: calculator.dataset.productId,
              variantId: variant.id,
              sku: variant.sku || '',
              quantity,
              price: Number(variant.price || 0) / 100,
              weight: itemWeight / 1000,
              length: Number(calculator.dataset.packageLength || 30),
              width: Number(calculator.dataset.packageWidth || 25),
              height: Number(calculator.dataset.packageHeight || 8)
            }]
          };
          submit?.setAttribute('aria-busy', 'true');
          if (submit) submit.disabled = true;
          renderShippingMessage(calculator, 'loading', calculator.dataset.loadingText || 'Calculando opções de entrega...');
          try {
            const response = await fetch(endpoint, {
              method: 'POST',
              credentials: 'same-origin',
              headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error('Shipping quote request failed');
            renderShippingRates(calculator, await response.json());
          } catch (error) {
            renderShippingMessage(calculator, 'error', errorText);
          } finally {
            submit?.removeAttribute('aria-busy');
            if (submit) submit.disabled = false;
          }
        });
      });

      optionGroups.forEach((group, position) => {
        $$('[data-option-value]', group).forEach((button) => button.addEventListener('click', () => {
          $$('[data-option-value]', group).forEach((item) => item.classList.remove('is-selected'));
          button.classList.add('is-selected');
          selected[position] = button.dataset.optionValue;
          const label = $('[data-selected-option]', group);
          if (label) label.textContent = button.dataset.optionValue;
          selectVariant();
        }));
        $('[data-option-select]', group)?.addEventListener('change', (event) => {
          selected[position] = event.target.value;
          const label = $('[data-selected-option]', group);
          if (label) label.textContent = event.target.value;
          selectVariant();
        });
      });
      $$('[data-product-quantity-change]', root).forEach((button) => button.addEventListener('click', () => {
        const current = Number.parseInt(quantityDisplays[0]?.value || quantityInputs[0]?.value || '1', 10);
        updateQuantity(current + Number(button.dataset.productQuantityChange));
      }));
      quantityDisplays.forEach((input) => input.addEventListener('change', () => updateQuantity(input.value)));
    });
    $$('[data-gallery-toggle]').forEach((button) => button.addEventListener('click', () => {
      const gallery = button.closest('[data-gallery-wrap]');
      const items = $$('[data-gallery-extra]', gallery);
      const expanded = button.getAttribute('aria-expanded') === 'true';
      items.forEach((item) => item.hidden = expanded);
      button.setAttribute('aria-expanded', String(!expanded));
      button.textContent = expanded ? button.dataset.moreText : button.dataset.lessText;
    }));
  };

  const initFacets = () => {
    $$('[data-facet-form] input, [data-facet-form] select').forEach((input) => input.addEventListener('change', () => input.form?.submit()));
    $('[data-filter-open]')?.addEventListener('click', () => {
      $('[data-facets-drawer]')?.classList.add('is-open');
      $('[data-drawer-overlay]').hidden = false;
    });
    $('[data-filter-close]')?.addEventListener('click', () => {
      $('[data-facets-drawer]')?.classList.remove('is-open');
      $('[data-drawer-overlay]').hidden = true;
    });
    $$('[data-grid-columns]').forEach((button) => button.addEventListener('click', () => {
      const grid = $('[data-collection-grid]');
      if (grid) grid.style.setProperty('--product-columns', button.dataset.gridColumns);
      $$('[data-grid-columns]').forEach((item) => item.classList.toggle('is-active', item === button));
    }));
  };

  const initModals = () => {
    $$('[data-modal-open]').forEach((button) => button.addEventListener('click', () => document.getElementById(button.dataset.modalOpen)?.classList.add('is-open')));
    $$('[data-modal-close]').forEach((button) => button.addEventListener('click', () => button.closest('.modal')?.classList.remove('is-open')));
    $$('.modal').forEach((modal) => modal.addEventListener('click', (event) => { if (event.target === modal) modal.classList.remove('is-open'); }));
  };

  const initRecommendations = () => {
    $$('[data-product-recommendations]').forEach(async (section) => {
      if (!section.dataset.url || section.children.length) return;
      try {
        const response = await fetch(section.dataset.url);
        if (!response.ok) return;
        const html = document.createElement('div');
        html.innerHTML = await response.text();
        const incoming = $('[data-product-recommendations]', html);
        if (incoming?.innerHTML.trim()) section.innerHTML = incoming.innerHTML;
      } catch (error) {
        // Recommendations are intentionally non-blocking.
      }
    });
  };

  const initComboBuilders = () => {
    $$('[data-combo-builder]:not([data-combo-bound])').forEach((root) => {
      root.dataset.comboBound = 'true';
      const items = $$('[data-combo-item]', root).filter((item) => $('[data-combo-variant]', item));
      const addButton = $('[data-combo-add]', root);
      const status = $('[data-combo-status]', root);
      const preview = $('[data-combo-preview]', root);

      const variantData = (item) => {
        const input = $('[data-combo-variant]', item);
        const option = input?.tagName === 'SELECT' ? input.selectedOptions[0] : input;
        return {
          id: input?.value,
          price: Number(option?.dataset.price || 0),
          compare: Number(option?.dataset.compare || 0),
          title: option?.dataset.title || '',
          image: option?.dataset.image || item.dataset.comboItemImage || ''
        };
      };

      const isSelected = (item) => item.dataset.required === 'true' || $('[data-combo-toggle]', item)?.checked === true;
      const quantity = (item) => Math.max(1, Number.parseInt($('[data-combo-quantity]', item)?.value || '1', 10));

      const update = () => {
        let total = 0;
        let count = 0;
        const selectedLines = [];
        items.forEach((item) => {
          const selected = isSelected(item);
          const current = variantData(item);
          const itemQuantity = quantity(item);
          const linePrice = current.price * itemQuantity;
          item.dataset.selected = String(selected);
          item.classList.toggle('is-unselected', !selected);
          const linePriceElement = $('[data-combo-item-price]', item);
          if (linePriceElement) linePriceElement.textContent = formatMoney(linePrice);
          $$('[data-installment-text]', item).forEach((installment) => {
            const text = $('span', installment);
            if (text) text.textContent = installmentMessage(linePrice, Number(installment.dataset.count || 10), installment.dataset.template);
          });
          if (selected) {
            total += linePrice;
            count += itemQuantity;
            selectedLines.push({
              title: item.dataset.comboItemTitle || 'Produto',
              variant: current.title,
              image: current.image,
              quantity: itemQuantity
            });
          }
        });
        const totalElement = $('[data-combo-total]', root);
        const countElement = $('[data-combo-count]', root);
        const installments = $('[data-combo-installments]', root);
        if (totalElement) totalElement.textContent = formatMoney(total);
        if (countElement) countElement.textContent = `${count} ${countElement.dataset.label || root.dataset.itemsLabel || 'itens selecionados'}`;
        if (installments) {
          const installmentCount = Number(installments.dataset.count || 10);
          installments.textContent = total > 0 ? installmentMessage(total, installmentCount, installments.dataset.template) : '';
        }
        if (preview) {
          preview.hidden = selectedLines.length === 0;
          preview.innerHTML = selectedLines.map((line) => {
            const variant = line.variant && line.variant !== 'Default Title' ? `${escapeHtml(line.variant)} · ` : '';
            const media = line.image ? `<img src="${escapeHtml(line.image)}" alt="" width="64" height="76" loading="lazy">` : '';
            return `<article class="combo-selection-card">${media}<div><strong>${escapeHtml(line.title)}</strong><span>${variant}Qtd. ${line.quantity}</span></div></article>`;
          }).join('');
        }
        if (addButton) addButton.disabled = count === 0;
      };

      $$('[data-combo-toggle], [data-combo-variant], [data-combo-quantity]', root).forEach((input) => {
        input.addEventListener('change', update);
      });
      $$('[data-combo-quantity-change]', root).forEach((button) => button.addEventListener('click', () => {
        const item = button.closest('[data-combo-item]');
        const input = $('[data-combo-quantity]', item);
        if (!input) return;
        const min = Number(input.min || 1);
        const max = Number(input.max || 99);
        input.value = Math.min(max, Math.max(min, quantity(item) + Number(button.dataset.comboQuantityChange || 0)));
        update();
      }));
      addButton?.addEventListener('click', async () => {
        const selectedItems = items.filter(isSelected).map((item) => ({
          id: variantData(item).id,
          quantity: quantity(item),
          properties: {
            _ivory_combo: root.dataset.comboId,
            _ivory_combo_title: root.dataset.comboTitle
          }
        })).filter((item) => item.id);
        if (!selectedItems.length) {
          if (status) status.textContent = root.dataset.emptyMessage || 'Selecione pelo menos um produto.';
          return;
        }
        addButton.setAttribute('aria-busy', 'true');
        if (status) status.textContent = '';
        try {
          await addItems(selectedItems);
          if (status) status.textContent = root.dataset.addedMessage || strings.added;
          showToast(root.dataset.addedMessage || strings.added);
          if (drawerEnabled()) setDrawerState(drawer(), true);
          else window.location.assign(routes.cart || '/cart');
        } catch (error) {
          const message = error.message || root.dataset.errorMessage;
          if (status) status.textContent = message;
          showToast(message);
        } finally {
          addButton.removeAttribute('aria-busy');
        }
      });
      update();
    });
  };

  document.addEventListener('submit', (event) => {
    const discountForm = event.target.closest('[data-discount-form]');
    if (discountForm) {
      event.preventDefault();
      const code = new FormData(discountForm).get('discount')?.toString().trim();
      if (!code) return;
      const root = String(routes.root || '/').replace(/\/?$/, '/');
      window.location.assign(`${root}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(`${root}checkout`)}`);
      return;
    }
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  });

  let noteTimer;
  document.addEventListener('input', (event) => {
    const note = event.target.closest('[data-cart-note]');
    if (!note) return;
    window.clearTimeout(noteTimer);
    const status = $('[data-cart-note-status]', note.closest('.drawer-card'));
    noteTimer = window.setTimeout(() => {
      saveCartNote(note.value, status).catch((error) => {
        if (status) status.textContent = error.message;
        showToast(error.message);
      });
    }, 500);
  });

  document.addEventListener('change', (event) => {
    const variantSelect = event.target.closest('[data-order-bump-variant]');
    if (!variantSelect) return;
    const offer = variantSelect.closest('[data-order-bump]');
    const option = variantSelect.selectedOptions[0];
    const price = Number(option?.dataset.price || 0);
    if (offer) offer.dataset.variantId = variantSelect.value;
    const priceElement = $('[data-order-bump-price]', offer);
    if (priceElement) priceElement.textContent = formatMoney(price);
    const installments = $('[data-order-bump-installments]', offer);
    if (installments) installments.textContent = installmentMessage(price, Number(installments.dataset.count || 10), installments.dataset.template);
  });

  document.addEventListener('click', (event) => {
    const changeButton = event.target.closest('[data-cart-change]');
    if (changeButton) {
      changeButton.setAttribute('aria-busy', 'true');
      changeCart(changeButton.dataset.cartChange, Number(changeButton.dataset.quantity))
        .catch((error) => showToast(error.message || drawer()?.dataset.updateError))
        .finally(() => changeButton.removeAttribute('aria-busy'));
      return;
    }
    const bumpButton = event.target.closest('[data-order-bump-add]');
    if (bumpButton) {
      const offer = bumpButton.closest('[data-order-bump]');
      const variantId = $('[data-order-bump-variant]', offer)?.value || offer.dataset.variantId;
      bumpButton.setAttribute('aria-busy', 'true');
      addVariant(variantId, { _ivory_offer: 'true' })
        .then(() => showToast(strings.added))
        .catch((error) => showToast(error.message || drawer()?.dataset.updateError))
        .finally(() => bumpButton.removeAttribute('aria-busy'));
      return;
    }
    const giftButton = event.target.closest('[data-gift-add]');
    if (giftButton) {
      const gift = giftButton.closest('[data-gift-block]');
      giftButton.setAttribute('aria-busy', 'true');
      addVariant(gift.dataset.variantId, { _ivory_gift: 'true', _ivory_gift_id: gift.dataset.blockId })
        .then(() => showToast(strings.added))
        .catch((error) => showToast(error.message || drawer()?.dataset.giftError))
        .finally(() => giftButton.removeAttribute('aria-busy'));
    }
  });

  document.addEventListener('keydown', (event) => {
    const cartDrawer = drawer();
    if (event.key === 'Escape') {
      if (cartDrawer?.classList.contains('is-open')) setDrawerState(cartDrawer, false);
      $('[data-mobile-menu]')?.classList.remove('is-open');
      $('[data-search-panel]')?.classList.remove('is-open');
      $$('.modal.is-open').forEach((modal) => modal.classList.remove('is-open'));
      return;
    }
    if (event.key !== 'Tab' || !cartDrawer?.classList.contains('is-open')) return;
    const focusable = $$('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', cartDrawer)
      .filter((element) => !element.hidden && element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('DOMContentLoaded', () => {
    initHeader(); initDrawers(); initHeroSliders(); initCarousels(); initProduct(); initComboBuilders(); initFacets(); initModals(); initRecommendations();
    updateShippingGoals(window.themeCartTotal || 0);
    updateCheckoutState();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initComboBuilders();
    if (event.target.querySelector('[data-cart-drawer]')) {
      initDrawers();
      getCart().then(() => setDrawerState(drawer(), true)).catch(() => {});
    }
  });

  document.addEventListener('shopify:block:select', (event) => {
    if (!event.target.closest('[data-cart-drawer]')) return;
    setDrawerState(drawer(), true);
    event.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  });
})();
